import { Routes } from '@angular/router';
import { AppleMapsChildRoutes, CampaignsChildRoutes, MainRoutes, ReviewInsightsChildRoutes } from './core/enums';
import { AuthGuard } from '@auth0/auth0-angular';
import { PermissionsGuard } from './core/permissions-guard';
import { LocationsSkeletonService } from './components/locations-skeleton/locations-skeleton.service';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { ReplyTemplateService } from './components/reviews/reply-template.service';
import { importProvidersFrom } from '@angular/core';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { LocationsSkeletonEffects } from './components/locations-skeleton/state/locations-skeleton-effects';
import { LocationListEffects } from './components/locations-skeleton/location-list/state/location-list-effects';
import { CCListEffects } from './components/locations-skeleton/cc-list/state/cc-list-effects';
import { reducers } from './store/locations';
import { reviewReducer } from './components/reviews/state/review-reducers';
import { ReviewEffects } from './components/reviews/state/review-effects';
import { aiInsightsReducer } from './components/ai-insights/state/ai-insights-reducer';
import { AiInsightsEffects } from './components/ai-insights/state/ai-insights-effects';
import { insightsReducer } from './components/review-insights/state/insights-reducer';
import { InsightsEffects } from './components/review-insights/state/insights-effects';
import { usersReducer } from './components/users/state/users-reducers';
import { UsersEffects } from './components/users/state/users-effects';
import { campaignEffects } from './components/campaigns/state/campaign-effects';
import { campaignReducer } from './components/campaigns/state/campaign-reducers';
import { AppleMapsOauthGuard } from './core/apple-maps-oauth-guard';
import { AiInsightsMessageParserService } from './components/ai-insights/ai-insights-message-parser.service';
import { canDeactivateGuard } from './core/can-deactivate.guard';

// WHEN ADDING A ROUTE:
// 1. Add the route to appRoutes (here)
// 2. Add to Navigation-Bar (navigation.component.html) if it should be accessible via the UI
// 3. Specify permissions to access the route (hasPermissionsForRoute in authentication.service.ts)
// 4. Specify NGRX https://dev.to/ngrx/using-ngrx-packages-with-standalone-angular-features-53d8
export const APP_ROUTES: Routes = [
  {
    path: MainRoutes.HOME,
    loadComponent: () => import('../app/components/home/home.component')
        .then(m => m.HomeComponent),
    title: MainRoutes.HOME,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
  },
  {
    path: MainRoutes.REVIEWS,
    loadComponent: () => import('./components/reviews/reviews.component')
        .then(m => m.ReviewsComponent),
    title: MainRoutes.REVIEWS,
    canActivate: [AuthGuard, PermissionsGuard],
    runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
    // providers for this route and its children. Having them here makes sure they will not be
    // bundled into a 'common bundle'. Should a member of another ROUTE require access to the same
    // service instance, move it out of this providers array and add `providedIn: 'root'` param in its
    // @Injectable() decorator- this will make it a SINGLETON service (otherwise a new, separate
    // instance would be created = NON-SINGLETON) which may lead to errors if the service is also
    // the single source of truth for other routes
    // https://angular.io/guide/singleton-services,
    // https://netbasal.com/angular-services-do-not-have-to-be-singletons-ffa879e62082
    providers: [
      ReplyTemplateService,
      importProvidersFrom(
          StoreModule.forFeature('review', reviewReducer),
          EffectsModule.forFeature([ReviewEffects]),
      ),
    ],
  },
  {
    path: MainRoutes.LOCATIONS,
    loadComponent: () => import('../app/components/locations-skeleton/locations-skeleton.component')
        .then(m => m.LocationsSkeletonComponent),
    title: MainRoutes.LOCATIONS,
    canActivate: [AuthGuard, PermissionsGuard],
    runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
    providers: [
      LocationsSkeletonService,
      // since we cannot pass form field appearance to <mat-paginator> yet
      // https://github.com/angular/components/issues/16266
      // gotta set a new default value for the whole module so <location-list>'s <mat-paginator> can be
      // styled consistently
      // it's still possible to pass different 'appearance' value to individual <mat-formfield>s
      {
        provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
        useValue: { appearance: 'fill' },
      },
      importProvidersFrom(
          StoreModule.forFeature('locations', reducers),
          EffectsModule.forFeature([LocationsSkeletonEffects, LocationListEffects, CCListEffects]),
      ),
    ],
  },
  {
    path: MainRoutes.AI_INSIGHTS,
    loadComponent: () => import('./components/ai-insights/ai-insights.component')
        .then(m => m.AiInsightsComponent),
    title: MainRoutes.AI_INSIGHTS,
    providers: [
      AiInsightsMessageParserService,
      importProvidersFrom(
          StoreModule.forFeature('aiInsights', aiInsightsReducer),
          EffectsModule.forFeature([AiInsightsEffects]),
      ),
    ],
    canActivate: [AuthGuard, PermissionsGuard],
    runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
  },
  {
    path: MainRoutes.INSIGHTS,
    loadComponent: () => import('./components/review-insights/review-insights.component')
        .then(m => m.ReviewInsightsComponent),
    title: MainRoutes.INSIGHTS,
    providers: [
      importProvidersFrom(
          StoreModule.forFeature('reviewInsights', insightsReducer),
          EffectsModule.forFeature([InsightsEffects]),
      ),
    ],
    children: [
      {
        path: '', // matches MainRoutes.INSIGHTS
        loadComponent: () => import('./components/review-insights/review-insights-overview/review-insights-overview.component')
            .then((m) => m.ReviewInsightsOverviewComponent),
        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
        title: '', // prevents creating a BC
      },
      {
        path: ReviewInsightsChildRoutes.NUMBER_OF_REVIEWS,
        loadComponent: () => import('./components/review-insights/review-number-details/review-number-details.component')
            .then((m) => m.ReviewNumberDetailsComponent),
        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
        title: ReviewInsightsChildRoutes.NUMBER_OF_REVIEWS,
      },
      {
        path: ReviewInsightsChildRoutes.RATINGS,
        loadComponent: () => import('./components/review-insights/rating-details/rating-details.component')
            .then((m) => m.RatingDetailsComponent),
        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
        title: ReviewInsightsChildRoutes.RATINGS,
      },
      {
        path: ReviewInsightsChildRoutes.LOCATION_COMPARISON,
        loadComponent: () => import('./components/review-insights/location-comparison-details/location-comparison-details.component')
            .then((m) => m.LocationComparisonDetailsComponent),
        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
        title: ReviewInsightsChildRoutes.LOCATION_COMPARISON,
      },
      {
        path: ReviewInsightsChildRoutes.POST_TIME,
        loadComponent: () => import('./components/review-insights/post-time-details/post-time-details.component')
            .then((m) => m.PostTimeDetailsComponent),
        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
        title: ReviewInsightsChildRoutes.POST_TIME,
      },
      {
        path: ReviewInsightsChildRoutes.RESPONDED_COMMENTED,
        loadComponent: () => import('./components/review-insights/responded-commented-details/responded-commented-details.component')
            .then((m) => m.RespondedCommentedDetailsComponent),
        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
        title: ReviewInsightsChildRoutes.RESPONDED_COMMENTED,
      },
      {
        path: ReviewInsightsChildRoutes.RESPONSE_TIME,
        loadComponent: () => import('./components/review-insights/response-time-details/response-time-details.component')
            .then((m) => m.ResponseTimeDetailsComponent),
        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
        title: ReviewInsightsChildRoutes.RESPONSE_TIME,
      }],
    canActivate: [AuthGuard, PermissionsGuard],
    runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
  },
  {
    path: MainRoutes.USERS,
    loadComponent: () => import('../app/components/users/users.component')
        .then(m => m.UsersComponent),
    title: MainRoutes.USERS,
    canActivate: [AuthGuard, PermissionsGuard],
    runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
    providers: [
      importProvidersFrom(
          StoreModule.forFeature('users', usersReducer),
          EffectsModule.forFeature([UsersEffects]),
      ),
    ],
  },
  {
    path: MainRoutes.POSTS,
    loadComponent: () => import('./components/campaigns/campaigns.component')
        .then(m => m.CampaignsComponent),
    title: MainRoutes.POSTS,
    canActivate: [AuthGuard, PermissionsGuard],
    runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
    providers: [
      importProvidersFrom(
          StoreModule.forFeature('campaign', campaignReducer),
          EffectsModule.forFeature([campaignEffects]),
      ),
    ],
    children: [
      {
        path: '', // matches /campaigns = the default view
        loadComponent: () => import('./components/campaigns/campaign-list/campaign-list.component')
            .then((m) => m.CampaignListComponent),
        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
        title: '', // prevents adding a BC
        children: [
          {
            path: CampaignsChildRoutes.DETAIL + '/:id',
            loadComponent: () => import('./components/campaigns/campaign-list/campaign-detail/campaign-detail.component')
                .then((m) => m.CampaignDetailComponent),
            runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
            title: '', // don't add a BC when it's just the side menu that is opened
          },
        ],
      },
      {
        path: CampaignsChildRoutes.CREATE,
        loadComponent: () => import('./components/campaigns/campaign-upsert/campaign-upsert.component')
            .then((m) => m.CampaignUpsertComponent),
        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
        title: CampaignsChildRoutes.CREATE,
        canDeactivate: [canDeactivateGuard],
      },
      {
        path: CampaignsChildRoutes.PLATFORMS,
        loadComponent: () => import('./components/campaigns/platforms/platforms.component')
            .then((m) => m.PlatformsComponent),
        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
        title: CampaignsChildRoutes.PLATFORMS,
        canActivate: [PermissionsGuard],
        canDeactivate: [canDeactivateGuard],
      },
      {
        path: CampaignsChildRoutes.DETAIL + '/:id',
        title: CampaignsChildRoutes.DETAIL,
        children: [
          {
            path: CampaignsChildRoutes.EDIT,
            loadComponent: () => import('./components/campaigns/campaign-upsert/campaign-upsert.component')
                .then((m) => m.CampaignUpsertComponent),
            runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
            title: CampaignsChildRoutes.EDIT,
            canDeactivate: [canDeactivateGuard],
          },
          {
            path: CampaignsChildRoutes.DUPLICATE,
            loadComponent: () => import('./components/campaigns/campaign-upsert/campaign-upsert.component')
                .then((m) => m.CampaignUpsertComponent),
            runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
            title: CampaignsChildRoutes.DUPLICATE,
            canDeactivate: [canDeactivateGuard],
          },
        ],
      },
    ],
  },
  {
    path: MainRoutes.APPLE_MAPS,
    canActivate: [AuthGuard], // TODO: add PermissionGuard? after we agree who should have access
    children: [
      {
        // /applemaps = invalid path for the moment that will route back to /home
        path: '',
        pathMatch: 'full',
        redirectTo: '/' + MainRoutes.HOME,
      },
      {
        path: AppleMapsChildRoutes.OAUTH,
        loadComponent: () => import('../app/components/apple-maps/oauth/oauth.component')
            .then(m => m.OauthComponent),
        canActivate: [AppleMapsOauthGuard], // check for valid 'success' query param
        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
      }],
  },
  {
    path: MainRoutes.GUIDE,
    loadComponent: () => import('../app/components/guide/guide.component')
        .then(m => m.GuideComponent),
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
    title: MainRoutes.GUIDE,
  },
  {
    path: MainRoutes.ERROR,
    loadComponent: () => import('../app/components/error-page/error-page.component')
        .then(m => m.ErrorPageComponent),
  },
  {
    path: '**', // Wildcard route - matches non-defined paths including ''
    redirectTo: MainRoutes.HOME,
    pathMatch: 'full',
  },
];
